import React, { useEffect, useRef, useState } from "react";
import "./MentorsList.css";
import Select from "react-select";
import FilterListIcon from "@mui/icons-material/FilterList";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import LanguageIcon from "@mui/icons-material/Language";
import WorkIcon from "@mui/icons-material/Work";
import CorporateFareSharpIcon from "@mui/icons-material/CorporateFareSharp";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  domainOptions,
  companyOptions,
  skillsOptions,
  languageOptions,
} from "../../../Data/SelectOptions";
import dummyProfileImage from "../../../assets/dummy-profile-pic.png";
import { getMentorList } from "../../../ApiServices/ApiServices";
import CodeQueueSpinner, { handleError } from "../../../Utils";
import CreatableSelect from "react-select/creatable";

const MentorsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLang, setSelectedLang] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [mentorList, setMentorList] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMentors, setTotalMentors] = useState(0);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isFetchingMore &&
          mentorList.length < totalMentors
        ) {
          setIsFetchingMore(true);
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [mentorList.length, totalMentors, isFetchingMore]);


  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDomain, selectedCompany, selectedSkills, selectedLang, keyword]);

  // Fetch mentors 
  useEffect(() => {
    document.title = "CodeQueue - Explore Mentors";
    const fetchMentors = async () => {
      setLoading(currentPage === 1);
      try {
        const data = await getMentorList({
          page: currentPage,
          limit: 10,
          domain: selectedDomain?.value,
          company: selectedCompany?.value,
          skills: selectedSkills.map(s => s.value),
          language: selectedLang.map(l => l.value),
          keyword,
        });
        if (currentPage === 1) {
          setMentorList(data.data);
        } else {
          setMentorList(prev => [...prev, ...data.data]);
        }
        setTotalMentors(data.total);
      } catch (error) {
        handleError(error?.response?.data?.message);
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    };
    fetchMentors();
  }, [currentPage, selectedDomain, selectedCompany, selectedSkills, selectedLang, keyword]);

  // // Infinite scroll
  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (
  //       window.innerHeight + window.scrollY >= document.body.offsetHeight - 200 &&
  //       !isFetchingMore &&
  //       mentorList.length < totalMentors
  //     ) {
  //       setIsFetchingMore(true);
  //       setCurrentPage((prev) => prev + 1);
  //     }
  //   };
  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, [mentorList.length, totalMentors, isFetchingMore]);

  const isProfileDetailsRoute = location.pathname.startsWith(
    "/mentors-list/details"
  );

  const goToDetails = (index) => {
    const mentor = mentorList[index];
    const mentorId = mentor._id;
    const mentorName = encodeURIComponent(mentor.name);
    navigate(`details?id=${mentorId}&name=${mentorName}`, {
      state: { mentor: mentorList[index] },
    });
  };

  const resetFilter = () => {
    setKeyword("");
    setSearchInput("");
    setSelectedDomain(null);
    setSelectedCompany(null);
    setSelectedSkills([]);
    setSelectedLang([]);
  };

  return (
    <>
      {!isProfileDetailsRoute && (
        <div className="mentor-profiles flex">
          {/* Left Section - Filters */}
          <div className="mentor-profiles-left bg-white shadow-xl rounded-lg p-6 max-w-xs w-full hidden lg:block">
            <div className="flex w-full justify-between">
              <h4 className="text-xl font-semibold text-gray-800 mb-6">
                Filter By
              </h4>
              <a
                className="mt-1 cursor-pointer underline"
                onClick={resetFilter}
              >
                Reset Filter
              </a>
            </div>

            {/* Keyword Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Keyword
              </label>
              <div className="relative w-full">
                <input
                  type="text"
                  className="keyword-input"
                  placeholder="e.g., JavaScript, Web Developer, Apple..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setKeyword(searchInput);
                  }}
                />
                <button
                  type="button"
                  className="search-btn-inside"
                  onClick={() => setKeyword(searchInput)}
                  aria-label="Search"
                  tabIndex={-1}
                >
                  <svg width="20" height="20" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="7" stroke="#6b7280" strokeWidth="2" />
                    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Companies Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Companies
              </label>
              <CreatableSelect
                options={companyOptions}
                placeholder="e.g., Amazon, Microsoft, etc."
                isSearchable
                value={selectedCompany}
                onChange={(option) => setSelectedCompany(option)}
                className="w-full"
                menuPortalTarget={document.body}
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              />
            </div>
            {/* Skills Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Skills
              </label>
              <CreatableSelect
                options={skillsOptions}
                placeholder="e.g., Java, DSA, SQL, etc."
                isSearchable
                value={selectedSkills}
                isMulti
                onChange={(options) => setSelectedSkills(options || [])}
                className="w-full"
                menuPortalTarget={document.body}
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              />
            </div>
            {/* Languages Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Language
              </label>
              <CreatableSelect
                options={languageOptions}
                placeholder="e.g., English, Hindi, Telugu, etc."
                isSearchable
                value={selectedLang}
                isMulti
                onChange={(options) => setSelectedLang(options || [])}
                className="w-full"
                menuPortalTarget={document.body}
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              />
            </div>
          </div>

          {/* Right Section - Mentor Profiles */}
          {loading ? (
            <span className="text-center w-full">
              <CodeQueueSpinner />
            </span>
          ) : (
            <div className="mentor-profiles-right flex-col">
              {mentorList?.map((item, index) => (
                <div
                  className="profile-shadow flex"
                  key={index}
                  onClick={() => goToDetails(index)}
                >
                  <div className="profile-shadow-left flex-col">
                    <div className="profile-card flex">
                      <div className="profile-card-left">
                        <img
                          src={item?.profileImage?.url || dummyProfileImage}
                          alt="img"
                        />
                      </div>
                      <div className="profile-card-right flex-col">
                        <div className="flex-col card-content">
                          <h3>{item?.name}</h3>
                          <div className="flex loca-rating-lang">
                            <div className="location">
                              <FmdGoodIcon className="location-icon" />
                              {item?.selectedState},{item?.selectedCountry}
                            </div>
                            <div className="location">
                              <LanguageIcon className="location-icon" />
                              English,{item?.selectedLanguage}
                            </div>
                          </div>
                          <div className="mentor-bio">
                            {item?.heading?.slice(0, 100)}
                            <span>...Read More</span>
                          </div>
                        </div>
                        <div className="flex loca-rating-lang">
                          <div className="location">
                            <WorkIcon className="location-icon" />
                            {item?.selectedExperience} of experience
                          </div>
                          <div className="location">
                            <CorporateFareSharpIcon className="location-icon" />
                            {item?.currentCompany}
                          </div>
                          <div className="location">
                            <PeopleAltIcon className="location-icon" />
                            {item?.selectedDesignation}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mentor-skills flex">
                      {item?.selectedSkills.slice(0, 5).map((val, i) => (
                        <div className="skills-key" key={i}>
                          {val}
                        </div>
                      ))}
                      <span>...Read More</span>
                    </div>
                  </div>
                  <div className="profile-card-min-price flex-col">
                    <strong>Starting</strong>
                    <strong>Price at</strong>
                    <strong>₹{item?.lowestPrice}</strong>
                    <span className="primary-btn">Book Session</span>
                  </div>
                </div>
              ))}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {isFetchingMore && (
                <div className="flex justify-center my-4">
                  <CodeQueueSpinner />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter Toggle Button for Small Screens */}
      {!isFilterOpen && !isProfileDetailsRoute && (
        <div
          className="lg:hidden fixed bottom-0 w-full text-center left-1/2 transform -translate-x-1/2 bg-gray-300 text-black p-3 rounded-lg shadow-lg cursor-pointer z-20"
          onClick={() => setIsFilterOpen(true)}
        >
          <FilterListIcon fontSize="medium" />
          <span>Filters</span>
        </div>
      )}

      {isFilterOpen && !isProfileDetailsRoute && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10">
          <div className="absolute bottom-0 bg-white w-full p-6 max-h-80 overflow-y-auto rounded-t-lg">
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 font-bold text-lg"
              onClick={() => setIsFilterOpen(false)}
            >
              ✕
            </button>
            <div className="flex w-full justify-between">
              <h4 className="text-xl font-semibold text-gray-800 mb-6">
                Filter By
              </h4>
              <a
                className="mt-1 cursor-pointer underline mr-8"
                onClick={resetFilter}
              >
                Reset Filter
              </a>
            </div>
            {/* Keyword Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Keyword
              </label>
              <div className="relative w-full">
                <input
                  type="text"
                  className="keyword-input"
                  placeholder="e.g., JavaScript, Web Developer, Apple..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setKeyword(searchInput);
                  }}
                />
                <button
                  type="button"
                  className="search-btn-inside"
                  onClick={() => setKeyword(searchInput)}
                  aria-label="Search"
                  tabIndex={-1}
                >
                  <svg width="20" height="20" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="7" stroke="#6b7280" strokeWidth="2" />
                    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Companies Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Companies
              </label>
              <Select
                options={companyOptions}
                placeholder="e.g., Amazon, Microsoft, etc."
                isSearchable
                value={selectedCompany}
                onChange={(option) => setSelectedCompany(option)}
                className="w-full"
              />
            </div>
            {/* Skills Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Skills
              </label>
              <Select
                options={skillsOptions}
                placeholder="e.g., Java, DSA, SQL, etc."
                isSearchable
                value={selectedSkills}
                isMulti
                onChange={(options) => setSelectedSkills(options || [])}
                className="w-full"
              />
            </div>
            {/* Languages Filter */}
            <div className="flex flex-col mb-6">
              <label className="text-sm font-medium text-gray-600">
                Languages
              </label>
              <Select
                options={languageOptions}
                placeholder="e.g., English, Hindi, Telugu, etc."
                isSearchable
                value={selectedLang}
                isMulti
                onChange={(options) => setSelectedLang(options || [])}
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
      <Outlet />
    </>
  );
};

export default MentorsList;